<template>
  <div class="login-form scrollbar">
    <form class="login-form__form" @submit.prevent="handleContactSubmit">
      <div class="row mt-3">
        <div class="col-6">
          <label class="required" for="contact-form-name">Name</label>
          <input
            id="contact-form-name"
            v-model="formData.name"
            type="text"
            placeholder="John Smith"
            class="form-input"
          >
        </div>
        <div class="col-6">
          <label class="required" for="contact-form-name">Email</label>
          <input
            id="contact-form-name"
            v-model="formData.email"
            type="text"
            placeholder="john.smith@company.com"
            class="form-input"
          >
        </div>
        <label class="required hpc" for="contact-form-country">Email</label>
        <input
          id="contact-form-country"
          v-model="formData.country"
          type="text"
          class="form-input hpc"
          placeholder="Enter your country"
        >
      </div>
      <div class="row mt-3">
        <div class="col-6">
          <label class="required" for="contact-form-name">Company</label>
          <input
            id="contact-form-name"
            v-model="formData.company"
            type="text"
            placeholder="Company name"
            class="form-input"
          >
        </div>
        <div class="col-6">
          <label class="required" for="contact-form-name">Title</label>
          <input
            id="contact-form-name"
            v-model="formData.title"
            type="text"
            placeholder="Lead Engineer"
            class="form-input"
          >
        </div>
      </div>
      <div class="row mt-3">
        <div class="col-12">
          <label class="required" for="contact-form-subject">Subject</label>
          <input
            id="contact-form-subject"
            v-model="formData.subject"
            type="text"
            placeholder="EdgeFirst Studio Demo"
            class="form-input"
          >
        </div>
      </div>
      <div class="row mt-3">
        <div class="col-12">
          <label class="required" for="form_create-org">Message</label>
          <textarea
            id="form_create-org"
            v-model="formData.message"
            type="text"
            placeholder="Hi, I am working on a vehicle detection solution and would like to get in contact."
            rows="5"
            class="form-input"
          />
        </div>
      </div>
      <div class="submit-container">
        <button type="submit" class="button">Send</button>
      </div>
    </form>
  </div>
</template>

<script setup>
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import { useToast } from 'vue-toast-notification';

import {
  ref,
} from 'vue';

const props = defineProps({
  initialSubject: {
    type: String,
    default: '',
  },
  initialMessage: {
    type: String,
    default: '',
  },
});

const $toast = useToast();

const formData = ref({
  name: '',
  email: '',
  company: '',
  title: '',
  subject: props.initialSubject,
  message: props.initialMessage,
  country: '', // Honeypot anti-spam field
});
const contactFormMessage = ref({
  message: '',
  isError: false,
});

async function handleContactSubmit() {
  contactFormMessage.value.message = '';
  contactFormMessage.value.isError = false;

  // Check if required fields are filled
  const {
    name, email, company, title, subject, message,
  } = formData.value;

  // List of required fields to check
  const requiredFields = {
    name, email, company, title, subject, message,
  };
  const missingFields = Object.keys(requiredFields).filter((field) => !requiredFields[field]);

  if (missingFields.length > 0) {
    contactFormMessage.value.message = `Please fill in the following fields: ${missingFields.join(', ')}`;
    contactFormMessage.value.isError = true;
    $toast.open({
      queue: true,
      duration: 5000,
      message: `Please fill in the following fields: ${missingFields.join(', ')}`,
      type: 'error',
      position: 'top',
    });
    return;
  }

  if (formData.value.country) {
    return;
  }

  const dataConnect = new DatastoreConnect();

  await dataConnect.userContact({ ...formData.value })
    .then((resp) => {
      if (resp.error !== undefined) {
        throw Error(resp.error.message);
      }

      contactFormMessage.value.message = resp.result;
      contactFormMessage.value.isError = false;
      $toast.open({
        queue: true,
        duration: 5000,
        message: "Request sent. Our team will reach out to you shortly.",
        type: 'success',
        position: 'top',
      });
    })
    .catch((error) => {
      contactFormMessage.value.message = error;
      contactFormMessage.value.isError = true;
      $toast.open({
        queue: true,
        duration: 5000,
        message: error,
        type: 'error',
        position: 'top',
      });
    });
}

</script>

<style lang="scss" scoped>

.login-form {
  width: 800px;
  padding: 10px 80px 60px 80px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  max-height: fit-content;
  min-height: fit-content;
  margin: auto;
  @include themify() {
    color: themed('body-text-color');
  }

  &__header {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    z-index: 10;
    margin-bottom: 10px;

    h1 {
      margin-top: 10px;
      text-align: center;
      font-size: 24px;
    }
  }
}

@media (max-width: $breakpoint-sm) {
  .login-form {
    padding: 32px;
  }
}
@media (max-width: $breakpoint-md) {
  .login-form {
    width: 100%;
  }
}

form {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;

  label {
    font-size: 16px;
    font-weight: bold;
    margin-bottom: 2px;

      &.required::after {
      content: '*';
      font-size: 14px;
      font-weight: 700;
      padding-left: 1px;
      @include themify() {
        color: themed('color-primary');
      }
    }
  }

  input {
    width: 100%;
    height: 42px;
    background: rgba(var(--color-white-rgb), 0.5);
    padding: 10px;
    border: 2px solid rgba(189, 186, 186, 0.75);
    border-radius: 8px;
    color: black;
    font-size: 16px;
    outline: none;

    &:focus, &:focus-visible {
      @include themify() {
        border: 2px solid themed('color-primary');
        box-shadow:  0 0px 2px themed('color-primary-300');
      }
      outline: 2px solid transparent;
      outline-offset: 2px;
    }
  }
  .form-input:not(:last-child) {
    margin-bottom: 16px;
  }

  textarea {
    width: 100%;
    height: unset;
    background: rgba(var(--color-white-rgb), 0.5);
    padding: 10px;
    border: 2px solid rgba(189, 186, 186, 0.75);
    border-radius: 8px;
    color: black;
    font-size: 16px;
    outline: none;

    &:focus, &:focus-visible {
      @include themify() {
        border: 2px solid themed('color-primary');
        box-shadow:  0 0px 2px themed('color-primary-300');
      }
      outline: 2px solid transparent;
      outline-offset: 2px;
    }
  }

  button[type=submit] {
    letter-spacing: 0.1em;
    width: 100%;
    height: 50px;
    border-radius: 6px;
  }

  .submit-container {
    margin-top: 16px;
    span._error {
      margin: 0 0 4px 0;
      font-weight: 400;
      font-size: 16px;
      text-align: left;
      margin-bottom: 0.5rem;
      @include themify() {
        color: themed('login-error');
      }
    }

    span._success {
      margin: 0 0 4px 0;
      font-weight: 400;
      font-size: 16px;
      text-align: left;
      margin-bottom: 0.5rem;
      @include themify() {
        color: themed('login-success');
      }
    }
  }
}

.hpc {
  display: none;
  opacity: 0;
  pointer-events: none;
}
</style>
